import { mergeWith, get, pick, omit } from 'lodash/object';
import { cloneDeep, cloneDeepWith } from 'lodash/lang';
import { BROWSER_STORAGE_ITEMS } from './enums';
import { mergeConfigs } from './substitutions';

export function omitDeep(collection, excludeKeys) {
  function omitFn(value) {
    if (value && typeof value === 'object') {
      excludeKeys.forEach((key) => {
        delete value[key];
      });
    }
  }

  return cloneDeepWith(collection, omitFn);
}

export function overwriteArrays(currentConfig, newConfig) {
  if (Array.isArray(newConfig)) {
    return newConfig;
  }
}

const getConfigWithCurrencySelectorOverrides = (
  fullConfig,
  currencySelector
) => {
  const clone = cloneDeep(fullConfig);
  if (currencySelector) {
    // eslint-disable-next-line no-unused-vars
    for (let key in clone) {
      mergeWith(
        clone[key],
        get(clone[key], `byCurrencySelector.${currencySelector}`, {}),
        overwriteArrays
      );
    }
  }
  return omitDeep(clone, ['byCurrencySelector']);
};

const getConfigWithNetworkOverrides = (fullConfig, paymentNetwork) => {
  const clone = cloneDeep(fullConfig);
  const paymentNetworkConfig = get(
    clone,
    `paymentNetworks.${paymentNetwork}`,
    {}
  );
  const configWithNetworkOverrides = mergeWith(
    clone,
    omit(paymentNetworkConfig, ['css']),
    overwriteArrays
  );
  return configWithNetworkOverrides;
};

const getFaqs = (faqs = {}, payment = {}) => {
  return []
    .concat(get(faqs, 'global') ? getFaqs(get(faqs, 'global'), payment) : [])
    .concat(get(faqs, 'program') ? getFaqs(get(faqs, 'program'), payment) : [])
    .concat(get(faqs, 'default', []))
    .concat(get(faqs, `byPaymentStatus.${payment.status}`, []))
    .concat(
      get(faqs, `byCurrencySelector.${payment.currencySelector}`)
        ? getFaqs(
            get(faqs, `byCurrencySelector.${payment.currencySelector}`),
            payment
          )
        : []
    );
};

const mergeFaqs = (configuration, payment) => {
  const { faqs } = configuration;
  const paymentNetwork = get(payment, 'paymentNetwork');
  const availablePaymentNetworks = get(payment, 'availablePaymentNetworks', []);

  let faqItems = getFaqs(faqs, payment);
  (paymentNetwork ? [paymentNetwork] : availablePaymentNetworks)
    .map(
      (paymentNetwork) =>
        get(configuration, `paymentNetworks.${paymentNetwork}.faqs`),
      []
    )
    .forEach((faqConfig) => {
      faqItems = faqItems.concat(getFaqs(faqConfig, payment));
    });

  return faqItems;
};

export const transformConfigBasedOnAppState = ({
  configuration,
  selectedPaymentNetwork,
  payment,
  accounts,
  selectedLanguage,
  ...other
}) => {
  const clone = cloneDeep(configuration);
  const configByLanguage = omit(
    mergeConfigs(clone, get(clone, ['byLanguageCode', selectedLanguage], {})),
    ['byLanguageCode']
  );
  // const paymentNetwork = selectedPaymentNetwork || (get(payment, 'status') !== 'PENDING_ACCEPTANCE' && get(payment, 'paymentNetwork')); // checking for payment status as it looks like the payment network is not reset on payments that fail and are returned to PENDING_ACCEPTANCE, so they come back with the last attempted payment network even after failure
  const paymentNetwork =
    get(payment, 'paymentNetwork') || selectedPaymentNetwork;
  const currencySelector = get(payment, 'currencySelector');
  const faqs = mergeFaqs(cloneDeep(configByLanguage), {
    ...payment,
    paymentNetwork,
  });

  let flattenedConfig = getConfigWithNetworkOverrides(
    configByLanguage,
    paymentNetwork
  );
  flattenedConfig = getConfigWithCurrencySelectorOverrides(
    flattenedConfig,
    currencySelector
  );
  const paymentCompleteConfig = get(
    flattenedConfig,
    `paymentComplete.${window.sessionStorage.getItem(BROWSER_STORAGE_ITEMS.INITIAL_COMPLETE) ? 'initial' : 'revisit'}`,
    {}
  );

  flattenedConfig.paymentComplete = {
    ...flattenedConfig.paymentComplete,
    ...paymentCompleteConfig,
  };

  flattenedConfig.faqs = faqs;

  let views = [
    'performHealthCheck',
    'validatePayment',
    'paywall',
    'recipientLogin',
    'acceptPayment',
    'pollingForComplete',
    'pendingTransaction',
    'networkSelectionAccepted',
    'paymentComplete',
    'header',
    'footer',
    'title',
    'loadingPage',
    'systemNotifications',
    'systemUnavailable',
    'twoFactorAuthentication',
    'onHold',
  ];
  let otherKeys = [
    'programName',
    'faqEntityName',
    'errors',
    'programId',
    'paymentNetworks',
    'i18n',
    'css',
    'faqs',
    'languageSelector',
  ];
  flattenedConfig = pick(flattenedConfig, [...views, ...otherKeys]);

  return {
    configuration: flattenedConfig,
    selectedPaymentNetwork,
    payment,
    paymentNetworkConfig: pick(
      get(flattenedConfig, `paymentNetworks.${paymentNetwork}`, {}),
      ['id', 'displayName']
    ),
    accountConfiguration:
      paymentNetwork && accounts
        ? get(
            accounts.filter(
              (account) => account.paymentNetwork === paymentNetwork
            )[0],
            'configuration',
            null
          )
        : null,
    accounts, // TODO: why are there both accounts and accountCredentials? can't we get by with just one or the other?
    selectedLanguage,
    ...other,
  };
};
